/** @jsx jsx */
import React, { forwardRef, type ReactNode } from 'react';
import { jsx, cx, cssMap } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Focusable, Pressable, Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { useIntl } from '@atlassian/jira-intl';
import { FieldDescriptionPopup } from '../../field-description-popup/index.tsx';
import messages from '../messages.tsx';

export type PickerButtonProps = {
	isInvalid: boolean;
	isSelected: boolean;
	children: ReactNode;
	testId: string;
	onClick: (event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	onClear?: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
	fieldDescription?: string | null;
	isIrremovable?: boolean;
	fieldInvalidLabel?: string;
};

const contentStyles = cssMap({
	base: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		height: '32px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		flexDirection: 'row',
		columnGap: token('space.075'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		textAlign: 'left',
		flex: 1,
	},
	emptySpace: {
		display: 'flex',
		width: '32px',
	},
	iconAfterWrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		columnGap: token('space.100'),
	},
	iconWrapper: {
		display: 'inline-flex',
		alignItems: 'center',
	},
	selected: {
		borderColor: token('color.border.selected'),
		color: token('color.text.selected'),
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	invalid: {
		borderColor: token('color.border.danger'),
		color: token('color.text.danger'),
		backgroundColor: token('color.background.danger'),
		'&:hover': {
			backgroundColor: token('color.background.danger.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.danger.pressed'),
		},
	},
});

const PickerButton = forwardRef<HTMLButtonElement, PickerButtonProps>(
	(
		{
			children,
			isInvalid,
			isSelected,
			onClick,
			onClear,
			testId,
			fieldDescription,
			fieldInvalidLabel,
			isIrremovable = false,
		}: PickerButtonProps,
		ref,
	) => {
		const { formatMessage } = useIntl();

		const IconsAfterContent = (
			<Box xcss={contentStyles.iconAfterWrapper}>
				{!isInvalid && fieldDescription && (
					<FieldDescriptionPopup
						fieldDescription={fieldDescription}
						isParentPickerActive={isSelected}
					/>
				)}
				{isInvalid && fieldInvalidLabel && (
					<Tooltip content={fieldInvalidLabel}>
						{(tooltipProps) => (
							<Focusable
								as="span"
								tabIndex={0}
								role="button"
								xcss={contentStyles.iconWrapper}
								{...tooltipProps}
							>
								<StatusErrorIcon label="error" color={token('color.icon.danger')} />
							</Focusable>
						)}
					</Tooltip>
				)}
				<ChevronDownIcon label="" size="small" />
			</Box>
		);

		const extraStyling = isInvalid ? 'invalid' : 'selected';

		const PickerButtonContent = (
			<Pressable
				onClick={onClick}
				ref={ref}
				testId={testId}
				xcss={cx(contentStyles.base, (isInvalid || isSelected) && contentStyles[extraStyling])}
			>
				{children}
				{IconsAfterContent}
			</Pressable>
		);

		return (
			<Inline space="space.050" grow="fill">
				{PickerButtonContent}
				{!isIrremovable && onClear ? (
					<IconButton
						appearance="subtle"
						icon={CrossIcon}
						label={formatMessage(messages.removeField)}
						onClick={onClear}
					/>
				) : (
					<Box xcss={contentStyles.emptySpace} />
				)}
			</Inline>
		);
	},
);

export default PickerButton;
